import { yupResolver } from "@hookform/resolvers/yup";
import { isString } from "lodash";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { ErrorModel } from "../../api/services/client/models";
import { IdNameList } from "../../api/services/common/models";
import {
  useCreateMdmOfficeMutation,
  useUpdateMdmOfficeById,
} from "../../api/services/mdmOffice/mdmOfficeApis";
import {
  InvoicingDetailsType,
  MdmCreateOfficeParams,
  MdmUpdateOfficeParams,
} from "../../api/services/mdmOffice/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiModal,
  WarningConfirmationModal,
} from "../../components";
import { STATUS_BADGE_TYPE } from "../../components/statusBadge/StatusBadge";
import { CountryField } from "../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  BE_ERROR_CODES,
  FORM_ERROR_TIMEOUT_SHORT,
} from "../../helpers/constants";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  getFieldUndefinedForPayloadParams,
  getParsedBEErrors,
  getValueOrBlank,
  getVatPostalDetailsFromCountryKey,
  hasKey,
  isNotEmpty,
  replaceHashWithValue,
} from "../../helpers/utils";
import useAccordion from "../../hooks/useAccordion";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { OfficeDetailsResponse } from "../mdmOfficeSearch/modelsAndSchemas/models";
import { AddressAndContactDetails } from "./components/addressAndContactDetails/AddressAndContactDetails";
import { InvoicingDetails } from "./components/invoicingDetails/InvoicingDetails";
import { OfficeDetails } from "./components/officeDetails/OfficeDetails";
import { MdmCreateOfficeWrapper } from "./MdmCreateOffice.style";
import {
  AccordionTitleKeys,
  CreateOfficeSectionTypes,
  MdmCreateOfficeFormKeys,
} from "./modelsAndSchemas/models";
import { MdmCreateOfficeSchema } from "./modelsAndSchemas/ValidationSchema";
import { mdmCreateOfficeSiteCoreKeys } from "./siteCore/SiteCoreKeys";

const initialAccordionStatus = {
  [AccordionTitleKeys.officeDetails]: true,
  [AccordionTitleKeys.addressAndContactDetails]: false,
  [AccordionTitleKeys.all]: false,
};

interface MdmCreateOfficeProps {
  open: boolean;
  onClose: () => void;
  onOfficeCreated?: (officeId?: string) => void;
  isEdit?: boolean;
  officeData?: OfficeDetailsResponse;
}

export const MdmCreateOffice: FC<MdmCreateOfficeProps> = ({
  open,
  onClose,
  onOfficeCreated,
  isEdit = false,
  officeData,
}) => {
  const {
    commonValidationMessages,
    fetchComponentTitle,
    commonLabels,
    useFetchLabel,
    fetchRichTexts,
    fetchValidationMessage,
  } = useSiteCore();

  const {
    createNewOfficeTitle,
    officeDetailsSectionHeader,
    addressAndContactDetailsSectionHeader,
    invoicingDetailsHeader,
    officeSavedMessage,
    officeUpdatedMessage,
    editOfficeHeader,
    areYouSureYouWantToUpdateOffice,
    officeCodeMustBeThreeDigitsMessage,
    theSelectedParentOfficeIsCurrentlyTaggedToAChildOffice,
    uncheckBillingOfficeConfirmationMessage,
    officeAlreadyClassifiedClientMessage,
  } = mdmCreateOfficeSiteCoreKeys;

  const getLabel = useFetchLabel(createNewOfficeTitle);

  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleSelectedAccordionChange,
  } = useAccordion(initialAccordionStatus);
  const defaultTaxRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const postalCodeRef = useRef<{
    setDefaultValue: (values?: string) => void;
  }>(null);

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState(false);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningErrorMessage, setWarningErrorMessage] = useState("");
  const [
    showHideInvoicingDetailConfirmationModal,
    setShowHideInvoicingDetailConfirmationModal,
  ] = useState(false);

  const [countryVatPostalData, setCountryVatPostalData] = useState<
    CountryField[]
  >([]);

  const createOfficeFormDefaultValues = {
    [MdmCreateOfficeFormKeys.officeName]: "",
    [MdmCreateOfficeFormKeys.parentOfficeId]: "",
    [MdmCreateOfficeFormKeys.officeCode]: "",
    [MdmCreateOfficeFormKeys.officeType]: "",
    [MdmCreateOfficeFormKeys.marketId]: "",
    [MdmCreateOfficeFormKeys.regionId]: "",
    [MdmCreateOfficeFormKeys.billingOfficeId]: "",
    [MdmCreateOfficeFormKeys.salesCreditOfficeId]: "",
    [MdmCreateOfficeFormKeys.currencyId]: "",
    [MdmCreateOfficeFormKeys.invoiceTemplateId]: "",
    [MdmCreateOfficeFormKeys.salesCreditTemplateId]: "",
    [MdmCreateOfficeFormKeys.defaultTax]: [],
    [MdmCreateOfficeFormKeys.statusId]: STATUS_BADGE_TYPE.active,
    [MdmCreateOfficeFormKeys.timeZoneKey]: "",
    [MdmCreateOfficeFormKeys.address1]: "",
    [MdmCreateOfficeFormKeys.address2]: "",
    [MdmCreateOfficeFormKeys.address3]: "",
    [MdmCreateOfficeFormKeys.countryId]: "",
    [MdmCreateOfficeFormKeys.city]: "",
    [MdmCreateOfficeFormKeys.state]: "",
    [MdmCreateOfficeFormKeys.postalCode]: "",
    [MdmCreateOfficeFormKeys.businessUnitId]: "",
    [MdmCreateOfficeFormKeys.isBillingOffice]: true,
    [MdmCreateOfficeFormKeys.isSalesCreditOffice]: true,
    [MdmCreateOfficeFormKeys.header]: "",
    [MdmCreateOfficeFormKeys.footer1]: "",
    [MdmCreateOfficeFormKeys.footer2]: "",
    [MdmCreateOfficeFormKeys.footer3]: "",
  };

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const methods = useForm({
    mode: "all",
    defaultValues: createOfficeFormDefaultValues,
    resolver: yupResolver(
      MdmCreateOfficeSchema({
        ...commonValidationMessages,
        mustBeThreeDigitsErrorMessage: getLabel(
          officeCodeMustBeThreeDigitsMessage,
        ),
      }),
    ),
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { touchedFields, errors, dirtyFields },
    trigger,
    formState,
    control,
  } = methods;

  const {
    mutateAsync: createMdmOfficeMutation,
    isPending: isCreateMdmOfficePending,
    isError: isCreateMdmOfficeError,
    isSuccess: isCreateMdmOfficeSuccess,
  } = useCreateMdmOfficeMutation();

  const {
    mutateAsync: updateMdmOfficeMutation,
    isPending: isUpdateMdmOfficePending,
    isError: isUpdateMdmOfficeError,
    isSuccess: isUpdateMdmOfficeSuccess,
    error: updateMdmOfficeError,
  } = useUpdateMdmOfficeById();

  const countryVatPostalDataJson = fetchRichTexts(
    commonSiteCoreKeys.countryBusinessRuleListParent,
    commonSiteCoreKeys.countryBusinessRuleList,
  );

  const sectionFieldsMap: Record<CreateOfficeSectionTypes, string[]> = {
    officeDetails: [
      MdmCreateOfficeFormKeys.officeName,
      MdmCreateOfficeFormKeys.parentOfficeId,
      MdmCreateOfficeFormKeys.officeCode,
      MdmCreateOfficeFormKeys.officeType,
      MdmCreateOfficeFormKeys.marketId,
      MdmCreateOfficeFormKeys.regionId,
      MdmCreateOfficeFormKeys.billingOfficeId,
      MdmCreateOfficeFormKeys.salesCreditOfficeId,
      MdmCreateOfficeFormKeys.currencyId,
      MdmCreateOfficeFormKeys.invoiceTemplateId,
      MdmCreateOfficeFormKeys.salesCreditTemplateId,
      MdmCreateOfficeFormKeys.defaultTax,
      MdmCreateOfficeFormKeys.statusId,
    ],
    addressDetails: [
      MdmCreateOfficeFormKeys.timeZoneKey,
      MdmCreateOfficeFormKeys.address1,
      MdmCreateOfficeFormKeys.address2,
      MdmCreateOfficeFormKeys.address3,
      MdmCreateOfficeFormKeys.countryId,
      MdmCreateOfficeFormKeys.city,
      MdmCreateOfficeFormKeys.state,
      MdmCreateOfficeFormKeys.postalCode,
      MdmCreateOfficeFormKeys.businessUnitId,
    ],
    invoicingDetails: [MdmCreateOfficeFormKeys.header],
  };

  const isBillingOffice = useWatch({
    control: control,
    name: MdmCreateOfficeFormKeys.isBillingOffice,
  });

  useEffect(() => {
    if (countryVatPostalDataJson) {
      const countryVatPostalDataJsonParsed: CountryField[] = JSON.parse(
        countryVatPostalDataJson,
      );
      setCountryVatPostalData(countryVatPostalDataJsonParsed);
    }
  }, [countryVatPostalDataJson]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isCreateUpdateOfficeData: {
          isVisible: isCreateMdmOfficePending || isUpdateMdmOfficePending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );

    return () => {
      dispatch(
        toggleLoader({ isCreateUpdateOfficeData: { isVisible: false } }),
      );
    };
  }, [isCreateMdmOfficePending, isUpdateMdmOfficePending]);

  useEffect(() => {
    if (isCreateMdmOfficeSuccess || isUpdateMdmOfficeSuccess) {
      onClose();
      displayToast(
        getLabel(isEdit ? officeUpdatedMessage : officeSavedMessage),
        "success",
      );
      onOfficeCreated?.(isEdit ? officeData?.id : undefined);
    }
  }, [isCreateMdmOfficeSuccess, isUpdateMdmOfficeSuccess]);

  const showErrorsFromBE = (error: ErrorModel) => {
    switch (error.errorCode) {
      case BE_ERROR_CODES.OFFICE_HAS_ACTIVE_CHILD_RECORDS:
        setWarningErrorMessage(
          getLabel(theSelectedParentOfficeIsCurrentlyTaggedToAChildOffice),
        );
        setShowWarningModal(true);
        break;
      case BE_ERROR_CODES.OFFICE_ALREADY_CLASSIFIED_CLIENT:
        setWarningErrorMessage(getLabel(officeAlreadyClassifiedClientMessage));
        setShowWarningModal(true);
        break;
    }
  };

  useEffect(() => {
    if (isUpdateMdmOfficeError && updateMdmOfficeError) {
      const error = getParsedBEErrors(updateMdmOfficeError)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
        return;
      }
    }
    if (isCreateMdmOfficeError || isUpdateMdmOfficeError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isCreateMdmOfficeError, isUpdateMdmOfficeError]);

  useEffect(() => {
    if (isEdit && officeData && isNotEmpty(officeData)) {
      const { officeDetails, addressAndContactDetails } = officeData;
      setValue(MdmCreateOfficeFormKeys.officeName, officeDetails.officeName);
      setValue(
        MdmCreateOfficeFormKeys.parentOfficeId,
        officeDetails.parentOffice.id,
      );
      setValue(MdmCreateOfficeFormKeys.officeCode, officeDetails.officeCode);
      setValue(MdmCreateOfficeFormKeys.officeType, officeDetails.officeType);
      setValue(MdmCreateOfficeFormKeys.marketId, officeDetails.market.id);
      setValue(MdmCreateOfficeFormKeys.regionId, officeDetails.region.id);
      setValue(
        MdmCreateOfficeFormKeys.billingOfficeId,
        officeDetails.billingOffice.id,
      );
      setValue(
        MdmCreateOfficeFormKeys.salesCreditOfficeId,
        officeDetails.salesCreditOffice.id,
      );
      setValue(MdmCreateOfficeFormKeys.currencyId, officeDetails.currency.id);
      setValue(
        MdmCreateOfficeFormKeys.invoiceTemplateId,
        getValueOrBlank(officeDetails?.invoiceTemplate?.id),
      );
      setValue(
        MdmCreateOfficeFormKeys.salesCreditTemplateId,
        getValueOrBlank(officeDetails?.salesCreditTemplate?.id),
      );

      setValue(MdmCreateOfficeFormKeys.statusId, officeDetails.statusId);
      setValue(
        MdmCreateOfficeFormKeys.timeZoneKey,
        addressAndContactDetails?.timeZone?.id ?? "",
      );
      setValue(
        MdmCreateOfficeFormKeys.address1,
        addressAndContactDetails.address1,
      );
      setValue(
        MdmCreateOfficeFormKeys.address2,
        getValueOrBlank(addressAndContactDetails?.address2),
      );
      setValue(
        MdmCreateOfficeFormKeys.address3,
        getValueOrBlank(addressAndContactDetails?.address3),
      );
      setValue(
        MdmCreateOfficeFormKeys.countryId,
        getValueOrBlank(addressAndContactDetails?.country?.id),
      );
      setValue(
        MdmCreateOfficeFormKeys.businessUnitId,
        getValueOrBlank(addressAndContactDetails?.businessUnit?.id),
      );
      setValue(MdmCreateOfficeFormKeys.city, addressAndContactDetails.city);
      setValue(
        MdmCreateOfficeFormKeys.state,
        getValueOrBlank(addressAndContactDetails?.state),
      );
      setTimeout(() => {
        setValue(
          MdmCreateOfficeFormKeys.postalCode,
          addressAndContactDetails.postalCode,
        );
      }, 1000);

      setValue(
        MdmCreateOfficeFormKeys.isBillingOffice,
        officeDetails?.isBillingOffice ?? false,
      );
      setValue(
        MdmCreateOfficeFormKeys.isSalesCreditOffice,
        officeDetails?.isSalesCreditOffice ?? false,
      );

      const taxIds =
        officeDetails.taxes?.map((tax: IdNameList) => tax.id) || [];
      setValue(MdmCreateOfficeFormKeys.defaultTax, taxIds);

      setValue(
        MdmCreateOfficeFormKeys.header,
        getValueOrBlank(officeData?.invoicingDetails?.header),
      );
      setValue(
        MdmCreateOfficeFormKeys.footer1,
        getValueOrBlank(officeData?.invoicingDetails?.footer1),
      );
      setValue(
        MdmCreateOfficeFormKeys.footer2,
        getValueOrBlank(officeData?.invoicingDetails?.footer2),
      );
      setValue(
        MdmCreateOfficeFormKeys.footer3,
        getValueOrBlank(officeData?.invoicingDetails?.footer3),
      );
    }
  }, [isEdit]);

  const getParams = () => {
    const {
      officeName,
      parentOfficeId,
      officeCode,
      officeType,
      marketId,
      regionId,
      billingOfficeId,
      salesCreditOfficeId,
      currencyId,
      invoiceTemplateId,
      salesCreditTemplateId,
      defaultTax,
      statusId,
      timeZoneKey,
      address1,
      address2,
      address3,
      countryId,
      city,
      state,
      postalCode,
      isBillingOffice,
      isSalesCreditOffice,
      businessUnitId,
      header,
      footer1,
      footer2,
      footer3,
    } = getValues();

    const invoicingDetails: InvoicingDetailsType | undefined = isBillingOffice
      ? {
          header: getValueOrBlank(header),
          footer1: getValueOrBlank(footer1),
          footer2: getValueOrBlank(footer2),
          footer3: getValueOrBlank(footer3),
        }
      : undefined;

    const params: MdmCreateOfficeParams = {
      officeDetails: {
        officeName,
        parentOfficeId: parentOfficeId || "",
        officeCode,
        officeType,
        marketId,
        regionId,
        billingOfficeId: billingOfficeId || "",
        salesCreditOfficeId: salesCreditOfficeId || "",
        currencyId,
        ...getFieldUndefinedForPayloadParams({
          invoiceTemplateId,
          salesCreditTemplateId,
        }),
        ...(defaultTax && {
          taxIds: defaultTax
            ?.filter((tax): tax is string => isString(tax))
            .map((tax) => tax),
        }),
        statusId,
        isBillingOffice,
        isSalesCreditOffice,
      },
      addressAndContactDetails: {
        address1,
        countryId,
        city,
        postalCode,
        timeZoneKey,
        businessUnitId,
        ...getFieldUndefinedForPayloadParams({
          address2,
          address3,
          state,
          isBillingOffice,
          isSalesCreditOffice,
        }),
      },
      invoicingDetails,
    };

    // If editing, we need to include the officeId in the params
    if (isEdit) {
      return {
        ...params,
        officeId: officeData?.id,
      } as MdmUpdateOfficeParams;
    }

    return params;
  };

  const checkPostalValidation = () => {
    const { countryId, postalCode } = getValues();
    let isValid = true;

    const addressCountryRules = getVatPostalDetailsFromCountryKey(
      countryId,
      countryVatPostalData,
    );
    if (addressCountryRules?.postalCode.isMandatory && !postalCode) {
      setTimeout(() => {
        setError(MdmCreateOfficeFormKeys.postalCode, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    } else if (errors.postalCode) {
      setTimeout(() => {
        setError(MdmCreateOfficeFormKeys.postalCode, errors.postalCode!);
      }, FORM_ERROR_TIMEOUT_SHORT);
      isValid = false;
    }

    return isValid;
  };

  const callCreateOfficeApi = () => {
    if (!checkPostalValidation()) {
      trigger();
      return;
    }
    const params = getParams();
    createMdmOfficeMutation(params);
  };

  const handleSaveClick = () => {
    if (isEdit) {
      setShowUpdateConfirmationModal(true);
    } else {
      handleSubmit(callCreateOfficeApi)();
    }
  };

  const handleSaveModalPrimaryButtonClick = () => {
    setShowUpdateConfirmationModal(false);
    const params = getParams();
    handleSubmit(() =>
      updateMdmOfficeMutation(params as MdmUpdateOfficeParams),
    )();
  };

  const handleCancelModalClose = () => {
    if (showCancelConfirmationModal || showUpdateConfirmationModal) {
      setShowCancelConfirmationModal(false);
      setShowUpdateConfirmationModal(false);
    }
  };

  const handleCancelModalPrimaryButtonClick = () => {
    setShowCancelConfirmationModal(false);
    onClose();
  };

  const handleCancelClick = () => {
    if (isNotEmpty(touchedFields) || isEdit) {
      setShowCancelConfirmationModal(true);
      return;
    }
    onClose();
  };

  const modalTitle = useMemo(() => {
    return isEdit && officeData
      ? replaceHashWithValue(
          getLabel(editOfficeHeader),
          officeData.officeDetails.officeName,
        )
      : fetchComponentTitle(createNewOfficeTitle);
  }, [isEdit]);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={getLabel(labelKey)} />;
  };

  const handleWarningPopup = () => {
    setShowWarningModal(false);
  };

  const hasErrors = (section: CreateOfficeSectionTypes) => {
    const fields = sectionFieldsMap[section];
    return fields.some(
      (field) => formState.errors[field as keyof typeof formState.errors],
    );
  };

  useEffect(() => {
    checkErrorsAndExpandSection();
  }, [formState.errors]);

  const checkErrorsAndExpandSection = () => {
    (Object.keys(sectionFieldsMap) as CreateOfficeSectionTypes[]).forEach(
      (sectionKey: CreateOfficeSectionTypes) => {
        if (hasErrors(sectionKey)) {
          switch (sectionKey) {
            case "officeDetails": {
              handleSelectedAccordionChange({ officeDetails: true });
              break;
            }
            case "addressDetails": {
              handleSelectedAccordionChange({ addressAndContactDetails: true });
              break;
            }
          }
        }
      },
    );
  };

  const onBillingOfficeCheckChange = (isBillingOfficeChecked: boolean) => {
    const isInvoiceDirty =
      dirtyFields[MdmCreateOfficeFormKeys.header] ||
      dirtyFields[MdmCreateOfficeFormKeys.footer1] ||
      dirtyFields[MdmCreateOfficeFormKeys.footer2] ||
      dirtyFields[MdmCreateOfficeFormKeys.footer3];
    if (!isBillingOfficeChecked && (isInvoiceDirty || isEdit)) {
      setShowHideInvoicingDetailConfirmationModal(true);
    }
  };

  const handleCancelInvoicingDetailModalClose = () => {
    setValue(MdmCreateOfficeFormKeys.isBillingOffice, true);
    if (showHideInvoicingDetailConfirmationModal) {
      setShowHideInvoicingDetailConfirmationModal(false);
    }
  };

  const handleInvoicingDetailModalPrimaryButtonClick = () => {
    setShowHideInvoicingDetailConfirmationModal(false);
    setValue(MdmCreateOfficeFormKeys.isBillingOffice, false);
    clearInvoicingDetailsFields();
  };

  const clearInvoicingDetailsFields = () => {
    setValue(MdmCreateOfficeFormKeys.header, "");
    setValue(MdmCreateOfficeFormKeys.footer1, "");
    setValue(MdmCreateOfficeFormKeys.footer2, "");
    setValue(MdmCreateOfficeFormKeys.footer3, "");
  };

  return (
    <MuiModal
      onClose={handleCancelClick}
      title={modalTitle}
      open={open}
      primaryButtonTitle={isEdit ? commonLabels.update : commonLabels.save}
      onPrimaryButtonClick={handleSaveClick}
      secondaryButtonTitle={commonLabels.cancel}
      onSecondaryButtonClick={handleCancelClick}
      hasLargeContent
      className="mdm-create-office-modal"
    >
      <MdmCreateOfficeWrapper>
        <FormProvider {...methods}>
          <MuiBox className="accordion-header">
            <AccordionHeader
              title={renderHeader(officeDetailsSectionHeader)}
              onChange={() =>
                handleIndividualAccordionChange(
                  AccordionTitleKeys.officeDetails,
                )
              }
              isExpanded={accordionStatus.officeDetails}
            >
              <OfficeDetails
                defaultTaxRef={defaultTaxRef}
                isEdit={isEdit}
                onBillingOfficeCheckChange={onBillingOfficeCheckChange}
              />
            </AccordionHeader>
          </MuiBox>

          {!!isBillingOffice && (
            <MuiBox className="accordion-header">
              <AccordionHeader
                title={renderHeader(invoicingDetailsHeader)}
                onChange={() =>
                  handleIndividualAccordionChange(
                    AccordionTitleKeys.invoicingDetails,
                  )
                }
                isExpanded={accordionStatus.invoicingDetails}
              >
                <InvoicingDetails />
              </AccordionHeader>
            </MuiBox>
          )}
          <MuiBox className="accordion-header">
            <AccordionHeader
              title={renderHeader(addressAndContactDetailsSectionHeader)}
              onChange={() =>
                handleIndividualAccordionChange(
                  AccordionTitleKeys.addressAndContactDetails,
                )
              }
              isExpanded={accordionStatus.addressAndContactDetails}
            >
              <AddressAndContactDetails
                postalCodeRef={postalCodeRef}
                isEdit={isEdit}
                countryVatPostalData={countryVatPostalData}
              />
            </AccordionHeader>
          </MuiBox>
        </FormProvider>

        {showCancelConfirmationModal && (
          <WarningConfirmationModal
            labelContent={commonLabels.cancelButtonActionText}
            show={showCancelConfirmationModal}
            onClose={handleCancelModalClose}
            onPrimaryButtonClick={handleCancelModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
        {showUpdateConfirmationModal && (
          <WarningConfirmationModal
            labelContent={getLabel(areYouSureYouWantToUpdateOffice)}
            show={showUpdateConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleCancelModalClose}
            onPrimaryButtonClick={handleSaveModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}

        {showWarningModal && (
          <WarningConfirmationModal
            show={showWarningModal}
            onClose={() => {
              setShowWarningModal(false);
            }}
            onPrimaryButtonClick={handleWarningPopup}
            showSecondaryButton={false}
            primaryButtonTitle={commonLabels.ok}
            labelContent={warningErrorMessage}
          />
        )}

        {showHideInvoicingDetailConfirmationModal && (
          <WarningConfirmationModal
            labelContent={getLabel(uncheckBillingOfficeConfirmationMessage)}
            show={showHideInvoicingDetailConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleCancelInvoicingDetailModalClose}
            onPrimaryButtonClick={handleInvoicingDetailModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </MdmCreateOfficeWrapper>
    </MuiModal>
  );
};
